import { css } from '@emotion/react'
import { InfoLabel, TableFooter, Text } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { AccordionControls } from 'components/accordion/useAccordionControl'
import { DateTime } from 'components/date'
import { TableBox } from 'components/table'
import { PageInfo, PageParams } from 'graphql/types.generated'
import { stringToCountList } from 'util/string/string-to-countlist'

import { TipoOrigemRecord, UltimasVisitasQueryResult } from '../model-visualizacao-imovel'
import { VisualizarVisitaListItemBody } from './VisualizarUltimasVisitasListItemBody'

export interface VisualizarUltimasVisitasPagedListProps extends AccordionControls {
  visitas: UltimasVisitasQueryResult
  pageInfo?: PageInfo
  isDomicilio: boolean
  isLoading: boolean
  expandedItems: string[]
  onChangePageParams(values: PageParams): void
}

export function VisualizarUltimasVisitasPagedList(props: Readonly<VisualizarUltimasVisitasPagedListProps>) {
  const { visitas, pageInfo, isDomicilio, isLoading, onChangePageParams, ...accordionProps } = props

  const handlePageChange = (page: number) => {
    onChangePageParams({
      size: props.pageInfo?.size,
      page,
    })
  }

  const sizeChange = (size: number) => {
    onChangePageParams({
      size,
      page: 0,
    })
  }

  return (
    <TableBox>
      <AccordionDataTable
        {...accordionProps}
        rows={visitas}
        components={{
          AccordionPanel: (row) => <VisualizarVisitaListItemBody {...row} isDomicilio={isDomicilio} />,
        }}
        loading={isLoading}
        columns={[
          {
            name: 'dataVisita',
            header: 'Data da visita',
            render: (row) => <DateTime format='DD/MM/YYYY' value={row?.dataVisita} />,
          },
          {
            name: 'desfecho',
            header: 'Desfecho',
            render: (row) => <Text>{row?.dimensaoDesfechoVisita?.descricao ?? 'Não informado'}</Text>,
          },
          {
            name: 'motivoVisita',
            header: 'Motivo da visita',
            render: (row) => (
              <Text>
                {stringToCountList(
                  ...(row?.motivoVisita ?? []),
                  ...(row?.acompanhamento ?? []),
                  ...(row?.buscaAtiva ?? []),
                  ...(row?.controleAmbiental ?? [])
                )}
              </Text>
            ),
          },
          {
            name: 'profissionalResponsavel',
            header: 'Profissional Responsável',
            render: (row) => (
              <InfoLabel title={row?.dimensaoProfissional?.nome ?? 'Não informado'}>
                <Text>{row?.dimensaoCbo?.nome ?? 'Não informado'}</Text>
              </InfoLabel>
            ),
          },
          {
            name: 'equipe',
            header: 'Equipe',
            render: (row) =>
              row?.dimensaoEquipe ? (
                <InfoLabel title={`${row?.dimensaoEquipe?.nome}`}>
                  <Text>{row?.dimensaoEquipe?.ine}</Text>
                </InfoLabel>
              ) : (
                <Text>Sem equipe</Text>
              ),
          },
          {
            name: 'origem',
            header: 'Origem',
            render: (row) => (
              <Text>{(row?.tipoOrigem?.nome && TipoOrigemRecord[row.tipoOrigem.nome]) ?? 'Não informado'}</Text>
            ),
          },
        ]}
      />
      <TableFooter
        style={css`
          border-top-width: 0;
        `}
        totalElements={pageInfo?.totalElements}
        totalPages={pageInfo?.totalPages}
        pageSize={pageInfo?.size}
        page={pageInfo?.number}
        sizeOptions={[5, 15, 25, 50]}
        onPageChange={handlePageChange}
        onSizeChange={sizeChange}
      />
    </TableBox>
  )
}
